import { useState } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Label,
  Button,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Calendar,
} from '@design-system';
import { Calendar as CalendarIcon, Search } from 'lucide-react';
import { format } from 'date-fns';
import { DateFilterState, DatePreset } from '../types/metrics-type';

interface MetricsDateFilterProps {
  dateFilter: DateFilterState;
  onPresetChange: (preset: DatePreset) => void;
  onCustomDatesChange: (startDate: Date, endDate: Date) => void;
}

const PRESET_OPTIONS: { value: DatePreset; label: string }[] = [
  { value: 'last7days', label: 'Last 7 days' },
  { value: 'last30days', label: 'Last 30 days' },
  { value: 'last90days', label: 'Last 90 days' },
  { value: 'thisYear', label: 'This year' },
  { value: 'custom', label: 'Custom range' },
];

export function MetricsDateFilter({
  dateFilter,
  onPresetChange,
  onCustomDatesChange,
}: MetricsDateFilterProps) {
  const [tempStartDate, setTempStartDate] = useState<Date | undefined>(dateFilter.startDate);
  const [tempEndDate, setTempEndDate] = useState<Date | undefined>(dateFilter.endDate);

  const handleApplyCustomDates = () => {
    if (tempStartDate && tempEndDate) {
      onCustomDatesChange(tempStartDate, tempEndDate);
    }
  };

  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end">
      <div className="flex-1">
        <Label
          htmlFor="date-preset"
          className="mb-2 block text-sm font-medium text-gray-700"
        >
          Date Range
        </Label>
        <Select
          value={dateFilter.preset}
          onValueChange={value => onPresetChange(value as DatePreset)}
        >
          <SelectTrigger
            id="date-preset"
            className="w-full sm:w-48"
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRESET_OPTIONS.map(option => (
              <SelectItem
                key={option.value}
                value={option.value}
              >
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {dateFilter.preset === 'custom' && (
        <>
          <div className="flex-1">
            <Label className="mb-2 block text-sm font-medium text-gray-700">Start Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {tempStartDate ? format(tempStartDate, 'PPP') : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0"
                align="start"
              >
                <Calendar
                  mode="single"
                  selected={tempStartDate}
                  onSelect={setTempStartDate}
                />
              </PopoverContent>
            </Popover>
          </div>

          <div className="flex-1">
            <Label className="mb-2 block text-sm font-medium text-gray-700">End Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="w-full justify-start text-left font-normal"
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {tempEndDate ? format(tempEndDate, 'PPP') : <span>Pick a date</span>}
                </Button>
              </PopoverTrigger>
              <PopoverContent
                className="w-auto p-0"
                align="start"
              >
                <Calendar
                  mode="single"
                  selected={tempEndDate}
                  onSelect={setTempEndDate}
                />
              </PopoverContent>
            </Popover>
          </div>

          <Button
            onClick={handleApplyCustomDates}
            disabled={!tempStartDate || !tempEndDate}
            className="flex items-center gap-2"
          >
            <Search className="h-4 w-4" />
            Apply
          </Button>
        </>
      )}
    </div>
  );
}
